/** Adapted from standalone-search f83fe2d1 via selected Registrar V2. See docs/delivery/iteration2/fusion-shared-kit/TRANSFER.json. */
import type { Column } from "@tanstack/react-table";
import { ArrowDown, ChevronsUpDown as ArrowSeparateVertical, ArrowUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface DataTableColumnHeaderProps<
  TData,
  TValue,
> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  sortAscLabel?: string;
  sortDescLabel?: string;
  sortClearLabel?: string;
}

const UNSORTED = "none";

function getSortIcon(sort: "asc" | "desc" | false | undefined) {
  switch (sort) {
    case "desc":
      return <ArrowDown />;
    case "asc":
      return <ArrowUp />;
    default:
      return <ArrowSeparateVertical />;
  }
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  sortAscLabel = "Ascending",
  sortDescLabel = "Descending",
  sortClearLabel,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }
  const sort = column.getIsSorted();
  // Name the order in effect on the trigger, so it is known without opening
  // the menu. Unsorted, that is the clear item's order when there is one.
  const activeLabel =
    sort === "asc"
      ? sortAscLabel
      : sort === "desc"
        ? sortDescLabel
        : sortClearLabel;
  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="data-[state=open]:bg-accent -ms-3 min-h-[var(--control-height-compact)]"
            aria-label={
              activeLabel ? `${title}, ${activeLabel.toLowerCase()}` : undefined
            }
          >
            <span>{title}</span>
            {getSortIcon(sort)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {/* The checked item is the order in effect; unsorted checks the
              clear item when there is one and nothing otherwise. */}
          <DropdownMenuRadioGroup
            value={sort || UNSORTED}
            onValueChange={(value) => {
              if (value === "asc") column.toggleSorting(false);
              else if (value === "desc") column.toggleSorting(true);
              else column.clearSorting();
            }}
          >
            <DropdownMenuRadioItem value="asc">
              <ArrowUp className="text-muted-foreground h-3.5 w-3.5" />
              {sortAscLabel}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="desc">
              <ArrowDown className="text-muted-foreground h-3.5 w-3.5" />
              {sortDescLabel}
            </DropdownMenuRadioItem>
            {sortClearLabel && (
              <DropdownMenuRadioItem value={UNSORTED}>
                <ArrowSeparateVertical className="text-muted-foreground h-3.5 w-3.5" />
                {sortClearLabel}
              </DropdownMenuRadioItem>
            )}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
